import * as React from 'react'
import * as ReactDOMServer from 'react-dom/server'
import * as qs from 'qs'
import { connect } from 'react-redux'
import { RootState } from 'pc/reducer'
import { styling } from 'common/utils'
import { highlight } from 'common/utils/highlight'
import { DynamicCx } from 'common/types'
import { SuggestionType, getNclick } from 'common/constants/suggestionType'
import { fetchCatalog } from 'common/services/autoComplete'
import {
  AutoCompleteState,
  deactivateAutoComplete,
  closeAutoComplete,
  setSelected,
} from 'common/ducks/gnb/autoComplete'
import OutsideClick from 'common/components/OutsideClick'
import * as s from './autoComplete.scss'

interface StateProps {
  autoComplete: AutoCompleteState
}

interface DispatchProps {
  deactivateAutoComplete: typeof deactivateAutoComplete
  closeAutoComplete: typeof closeAutoComplete
  setSelected: typeof setSelected
}

interface OwnProps {
  cx?: DynamicCx
  onSearch: Function
  onSaveKeywordInfo: Function
}

interface OwnState {
  currentIndex: number
  catalog: string
}

type Props = StateProps & DispatchProps & OwnProps

@styling(s)
class AutoComplete extends React.Component<Props, OwnState> {
  keywordListHtml: object[][]

  constructor(props) {
    super(props)

    this.keywordListHtml = []
    this.state = {
      currentIndex: -1,
      catalog: '',
    }
  }

  // down key로 리스트 이동할 경우 state로 현재 index 주입
  static getDerivedStateFromProps(nextProps: Props, prevState: OwnState) {
    return {
      currentIndex: nextProps.autoComplete.selected,
    }
  }

  async componentDidUpdate(prevProps: Props, prevState: OwnState) {
    const { autoComplete } = this.props
    const { selected } = autoComplete
    const prevIndex = prevState.currentIndex
    const { currentIndex } = this.state

    if (currentIndex < 0) {
      // 진입 전
      // 이전에 리스트에 포커스 되어 있던 경우에는 상시 catalog를 비워준다 -> 레이어 닫아야 함
      if (prevIndex > 0) this.removeCatalog()
      return
    }

    if (prevIndex === currentIndex) return // 불필요한 추가 렌더링 막기

    const data = [].concat(...this.keywordListHtml)
    const selectedData = data[currentIndex]

    if (!selectedData) return

    const keyword = selectedData.props['data-keyword']

    // keydown으로 진입한 경우에 input에 반영
    if (prevIndex !== selected) {
      const categoryId = selectedData.props['data-categoryid']
      this.props.onSaveKeywordInfo({ categoryId, keyword })
    }

    // 원부 데이터 노출
    const hasCatalog = selectedData.props['data-hascatalog']

    if (hasCatalog) {
      const result = await Promise.all([fetchCatalog(keyword)])
      this.setState({ catalog: result && result[0] })
      return
    }

    this.setState({ catalog: selectedData.props['data-catalog'] })
  }

  handleClickKeyword = async e => {
    e.preventDefault()
    const linkTarget = e.currentTarget
    const keyword = linkTarget.getAttribute('data-keyword')
    const categoryId = linkTarget.getAttribute('data-categoryid') || ''

    const keywordInfo = { keyword, categoryId }
    await this.props.onSaveKeywordInfo(keywordInfo)
    this.props.onSearch(keywordInfo)
  }

  handleMouseOverItem = async e => {
    e.preventDefault()
    const { setSelected } = this.props
    const linkTarget = e.currentTarget
    const currentIndex = Number(linkTarget.getAttribute('data-index'))
    const hasCatalog = JSON.parse(linkTarget.getAttribute('data-hascatalog'))

    // setSelected(-1) -> mouseover시에 down key로 이동한 index를  초기화 하기 위함
    if (hasCatalog) {
      const keyword = linkTarget.getAttribute('data-keyword')
      const result = await Promise.all([fetchCatalog(keyword)])
      this.setState({ currentIndex, catalog: result && result[0] }, () => setSelected(-1))
      return
    }

    this.setState({ currentIndex, catalog: linkTarget.getAttribute('data-catalog') }, () => setSelected(-1))
  }

  // 자동완성 창에서 mouse leave시에,
  // 자동완성이 열렸다가 -> 닫힌 시점에 catalog레이어를 닫아야 한다
  removeCatalog = () => {
    this.state.catalog && this.setState({ catalog: '' })
  }

  handleDeactivate = e => {
    this.props.deactivateAutoComplete()
    this.props.closeAutoComplete()
  }

  // 여러개의 쿼리를 가져와서 하이라이팅 해준다
  // ex) '고구마'의 쿼리 : ['고구마', 'rhrnak']
  //     'rhrnak'의 경우에도 '고구마'에 하이라이팅 되어야 한다
  getHighlightedKeyword = itemKeyword => {
    const { autoComplete } = this.props
    const { keywordQuery } = autoComplete

    const highlightedKeyword =
      keywordQuery
        .map(query => highlight({ query, item: itemKeyword, tag: 'strong' }))
        .filter(keyword => keyword !== itemKeyword)
        .shift() || itemKeyword

    return highlightedKeyword
  }

  render() {
    const { autoComplete, cx } = this.props
    const { currentIndex, catalog } = this.state
    const { isOpen, data, totalCount } = autoComplete

    /**
     * 2차원 배열을 리스트로 생성할때 사용하는 변수
     * 다른 <ul>로 렌더링 되어야 하고, index는 이어져야 하므로 해당 변수 사용
     * [['a','b'], ['c','d']]
     * <ul>
     *   <li index=0>a</li>
     *   <li index=1>b</li>
     * </ul>
     * <ul>
     *   <li index=2>c</li>
     *   <li index=3>d</li>
     * </ul>
     */
    let itemIndex = -1

    this.keywordListHtml = totalCount > 0 && [
      data[SuggestionType.WINDOW].map(item => (
        <a
          href={item.url}
          data-keyword={item.keyword}
          onClick={this.handleClickKeyword}
          onMouseOver={this.handleMouseOverItem}
          className={cx('link_window', `N=a:scb.${getNclick(SuggestionType.WINDOW)}`, `@${item.name}`)}>
          <em>
            <strong>{item.keyword} 바로가기</strong>
          </em>
        </a>
      )),
      data[SuggestionType.KEYWORD].map((item, index) => {
        const highlightedKeyword = this.getHighlightedKeyword(item.keyword)
        const hasCatalog = item.hasCatalog === 'Y' // 원부 데이터 가지고 있는지 여부
        return (
          <a
            key={`keyword-type${index}`}
            href="javascript:void(0)"
            data-keyword={item.keyword}
            data-hascatalog={hasCatalog}
            onClick={this.handleClickKeyword}
            onMouseOver={this.handleMouseOverItem}
            className={cx(`N=a:scb.item,r:${index + 1}`)}>
            <em dangerouslySetInnerHTML={{ __html: highlightedKeyword }} />
            {hasCatalog && <span className={cx('search_expand')}>확장검색</span>}
          </a>
        )
      }),
      data[SuggestionType.PLAN].map((item, index) => {
        const splittedUrl = item.url.split('?')
        const { seq } = splittedUrl && qs.parse(splittedUrl[1])

        const planCatalogElement = (
          <div className={cx('expand_inner')}>
            <a href={item.url} className={cx('goods')}>
              <img src={item.imageUrl} width="100" height="100" alt={`${item.keyword} 상품 이미지`} />
              <strong className={cx('theme_name')}>{item.keyword}</strong>
              <div className={cx('date')}>{item.date}</div>
              <span className={cx('compare')}>기획전 바로가기</span>
            </a>
          </div>
        )

        const highlightedKeyword = this.getHighlightedKeyword(item.keyword)
        return (
          <a
            key={`plan-type${index}`}
            href="javascript:void(0)"
            data-keyword={item.keyword}
            data-catalog={ReactDOMServer.renderToStaticMarkup(planCatalogElement)}
            onClick={this.handleClickKeyword}
            onMouseOver={this.handleMouseOverItem}
            className={cx(`N=a:sch.event,i:${seq}`)}>
            <span className={cx('expand_theme')}>기획전</span>
            <em dangerouslySetInnerHTML={{ __html: highlightedKeyword }} />{' '}
            <span className={cx('search_expand')}>확장검색</span>
          </a>
        )
      }),
      data[SuggestionType.CATEGORY].map((item, index) => {
        const highlightedKeyword = this.getHighlightedKeyword(item.keyword)
        return (
          <a
            key={`category-type${index}`}
            href="javascript:void(0)"
            data-keyword={item.keyword}
            data-categoryid={item.categoryId}
            dangerouslySetInnerHTML={{ __html: highlightedKeyword }}
            onClick={this.handleClickKeyword}
            onMouseOver={this.handleMouseOverItem}
            className={cx(`N=a:sch.accat,i:${item.categoryId},r:${index + 1}`)}
          />
        )
      }),
    ]

    return (
      <OutsideClick
        onClickOutside={this.props.closeAutoComplete}
        isRendered={autoComplete.isOpen}
        stopPropagation={false}>
        <div
          className={cx('layer_auto', {
            '@active': isOpen,
            '@selected': catalog,
          })}
          onMouseLeave={this.removeCatalog}>
          <div className={cx('basis_result', { '@active': isOpen })}>
            {totalCount < 1 ? (
              <p className={cx('basis_text')}>현재 자동완성 기능을 사용하고 있습니다.</p>
            ) : (
              <div>
                {this.keywordListHtml.map((itemArray, index) => {
                  if (itemArray.length > 0) {
                    return (
                      <ul key={`group${index}`}>
                        {itemArray.map(item => {
                          itemIndex = itemIndex + 1
                          return (
                            <li
                              key={`keyword${itemIndex}`}
                              data-index={itemIndex}
                              className={`${currentIndex === itemIndex ? 'active' : ''}`}>
                              {item}
                            </li>
                          )
                        })}
                      </ul>
                    )
                  }
                })}
              </div>
            )}
          </div>
          <div className={cx('expand_result', { active: catalog })} dangerouslySetInnerHTML={{ __html: catalog }} />
          <div className={cx('help_box')}>
            <a
              href="https://help.naver.com/support/contents/contents.nhn?serviceNo=606&categoryNo=1994"
              target="_blank"
              className={cx('@N=a:SNB.sch.achelp')}>
              도움말
            </a>
            <button type="button" onClick={this.handleDeactivate} className={cx('@N=a:sch.acoff')}>
              기능끄기
            </button>
            <div className={cx('layer_help')}>
              기능을 다시 켤 때는 <span className={cx('ico_srh')}>검색창</span>을 클릭하세요
            </div>
          </div>
        </div>
      </OutsideClick>
    )
  }
}

export default connect<StateProps, DispatchProps, OwnProps>(
  (state: RootState) => ({
    autoComplete: state.autoComplete,
  }),
  {
    deactivateAutoComplete,
    closeAutoComplete,
    setSelected,
  },
)(AutoComplete)
